import { ResourceHelperService } from '../resource-helper.service';
import { QueryBuilder } from '../query-builder.service';

/**
 * A dataservice that provides CRUD operations on journey entries against the backend Customer service
 */
export class JourneyService {
  public journeySummary: InterACT.Interfaces.IResource<InterACT.Interfaces.Journeys.SummaryResponse>;
  public journeySummation: InterACT.Interfaces.IResource<InterACT.Interfaces.Journeys.SummationResponse>;
  public resource: InterACT.Interfaces.IResource<InterACT.Interfaces.Journeys.JourneyResponse | InterACT.Interfaces.Journeys.JourneysResponse>;
  public matchJourneys: InterACT.Interfaces.IResource<InterACT.Interfaces.Journeys.JourneyResponse>;
  constructor(
    private $resource: angular.resource.IResourceService,
    private resourceHelperService: ResourceHelperService,
    private queryBuilder: QueryBuilder
  ) {
    this.journeySummary = $resource('api/journeys/summary');
    this.matchJourneys = $resource('api/itsoJourneyMatchRequests');
    this.journeySummation = $resource('api/journeys/summation');
    this.resource = $resource('api/journeys/:journeyId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true }
    );
  }

  /**
   * Function to get a summation of a property on a journey resource.
   *
   * @param qParam used to filter our query and return the correct results
   * @param grouping used to group and sum the results
   * @returns An object with a journey summation
   */
  public getSummation(qParam: string, grouping?: string) {
    return this.journeySummation.get({ q: qParam, grouping: grouping }).$promise;
  }

  /**
   * Function to retrieve a record from the Journey backend service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The number of items per page to be retrieved
   * @returns An array of journey records
   */
  public getList(page: number, perPageCount: number, searchText: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText);
    return this.resource.get(options).$promise;
  }

  /**
   * Function to retrieve a single Journey record from the backend service
   *
   * @param id The id of the Journey to get
   *
   * @returns A Journey record
   */
  public getJourney(id: string) {
    return this.resource.get({ journeyId: id }).$promise;
  }

  /**
   * Function to get journeySummary
   *
   * @param qParam used to filter our query and return the correct results
   * @param grouping used to group the results
   * @returns An object with a number of journeySummary
   */
  public getJourneySummary(qParam: string, grouping?: string) {
    const options = { q: qParam, grouping: grouping };
    return this.journeySummary.get(options).$promise;
  }

  /**
   * Creates a request to match Operators on an ITSO Journey. This uses the supplied start and
   * end date time values to build query used by the backend service to find journeys
   * within this date\time range.
   *
   * @param startDateTime An ISO8601 formatted datetime string
   * @param endDateTime An ISO8601 formatted datetime string
   * @returns A promise object representing the underlying HTTP request\response action
   */
  public requestJourneyMatch(startDateTime, endDateTime) {
    const query = this.queryBuilder.newParam()
      .isGreaterOrEqualTo('startDateTime', startDateTime)
      .and().isLessThan('startDateTime', endDateTime)
      .and().openSubQuery()
      .not().exists('service.link')
      .or().not().exists('operator.link')
      .closeSubQuery();
    return this.matchJourneys.save({ query: query.getParam() }).$promise;
  }
}
